import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { TireSeason } from '@navio/nsdk';
import { brandLabels } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiCard, NoddiSeparator } from 'noddi-ui-web';
import {
  CustomDateFormats,
  formatCurrencyAmount,
  formatPhoneNumber,
  getStruckThroughCatalogPrice,
  type MoneyWithOptionalCurrency,
  noddiFormatDate,
  useReservationCountdown
} from 'noddi-util';
import { CatalogPrice, DepartmentOrganizationName, NavioTireSeasonChip, PriceListDiscountLabel } from 'noddi-web';
import type { ReactNode } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import type { StationarySummaryLineItem } from '../../../utils/stationarySummaryLineItems';
import { ServiceCenterLocation } from '../../ServiceCenter/ServiceCenterLocation';
import { CheckoutDiscountRow } from '../../Summary/CheckoutDiscountRow';

type OrderDiscount = {
  key: string;
  label: string;
  amount: MoneyWithOptionalCurrency;
  imageUrl?: string | null;
};

type Props = {
  customerName?: string;
  customerPhoneNumber?: string;
  customerIsDriver?: boolean;
  organizationName?: string;
  departmentName?: string;
  locationName: string;
  locationAddress?: { displayText: string; googleMapsUrl: string };
  timeWindow?: { startsAt: string; endsAt: string };
  reservedUntil?: string;
  timeWindowAction?: ReactNode;
  carDisplayName: string;
  /** The wheel set the garage stores, when the booking signs the car up for the tire hotel. */
  incomingTireSeason?: TireSeason | null;
  /** Replaces the default customer + service center block, to match a flow's own header. */
  header?: ReactNode;
  /** Rendered after the total, e.g. how the order is paid. */
  footerNotice?: ReactNode;
  lineItems: StationarySummaryLineItem[];
  discounts?: OrderDiscount[];
  totalPrice: MoneyWithOptionalCurrency;
};

export const StationaryOrderSummaryCard = ({
  customerName,
  customerPhoneNumber,
  customerIsDriver,
  organizationName,
  departmentName,
  locationName,
  locationAddress,
  timeWindow,
  reservedUntil,
  timeWindowAction,
  carDisplayName,
  incomingTireSeason,
  header,
  footerNotice,
  lineItems,
  discounts,
  totalPrice
}: Props) => {
  const { currentBrand } = useCurrentBrand();
  const countdownDisplay = useReservationCountdown(reservedUntil);
  const priceListDiscountLabel = getCommonTranslations().priceListDiscountLabel;

  const timeWindowDisplay = timeWindow
    ? `${noddiFormatDate(new Date(timeWindow.startsAt), CustomDateFormats.FULL_DAY_MONTH)}, ${noddiFormatDate(new Date(timeWindow.startsAt), CustomDateFormats.TIME)}–${noddiFormatDate(new Date(timeWindow.endsAt), CustomDateFormats.TIME)}`
    : null;

  return (
    <NoddiCard contentClassName='flex flex-col gap-3' title={t`Your order`}>
      {header ?? (
        <>
          {customerName && (
            <>
              <div className='flex items-center gap-2'>
                <NoddiIcon name='User' />
                <span>{customerName}</span>
                {customerIsDriver && <span className='text-sm text-muted-foreground'>({t`Driver`})</span>}
              </div>
              {customerPhoneNumber && (
                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <NoddiIcon name='Phone' size='small' />
                  <span>{formatPhoneNumber(customerPhoneNumber)}</span>
                </div>
              )}
              {organizationName && (
                <p className='flex items-start gap-1.5 text-sm text-muted-foreground'>
                  <NoddiIcon name='Building2' size='small' />
                  <span>
                    <Trans>On behalf of</Trans>
                  </span>
                  {departmentName ? (
                    <DepartmentOrganizationName departmentName={departmentName} organizationName={organizationName} />
                  ) : (
                    organizationName
                  )}
                </p>
              )}
              <NoddiSeparator />
            </>
          )}

          {locationAddress && (
            <ServiceCenterLocation
              name={locationName}
              address={locationAddress}
              brandLabel={brandLabels[currentBrand]}
              showLabel
            />
          )}
        </>
      )}
      {header && <NoddiSeparator />}

      {(timeWindowDisplay || timeWindowAction) && (
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex items-center gap-2'>
              <NoddiIcon name='Calendar' />
              {timeWindowDisplay ? (
                <p className='font-semibold'>
                  {timeWindowDisplay.charAt(0).toUpperCase() + timeWindowDisplay.slice(1)}
                </p>
              ) : (
                <p>
                  <Trans>Choose a new time</Trans>
                </p>
              )}
            </div>
            {timeWindowAction}
          </div>
          {timeWindowDisplay && countdownDisplay && (
            <p className='text-sm text-muted-foreground'>
              <Trans>Booking slot reserved</Trans> ({countdownDisplay})
            </p>
          )}
        </div>
      )}
      <NoddiSeparator />

      <div className='flex flex-col gap-2'>
        <h2 className='font-semibold'>{carDisplayName}</h2>
        <div className='flex flex-col'>
          {lineItems.map((item) => {
            const struck = getStruckThroughCatalogPrice({
              payable: item.grossAmount,
              original: item.originalGrossAmount
            });
            const showPrice = item.grossAmount.amount > 0 || struck != null;
            return (
              <div key={item.key} className='flex justify-between gap-4'>
                <span>{item.name}</span>
                <span className='flex flex-col items-end'>
                  {showPrice && (
                    <span className='flex items-end gap-2'>
                      <span className='text-sm text-muted-foreground'>{item.quantity}x</span>
                      <CatalogPrice
                        payable={item.grossAmount}
                        original={item.originalGrossAmount}
                        align='end'
                        freeLabel={t`Free`}
                      />
                    </span>
                  )}
                  <PriceListDiscountLabel
                    priceListName={item.priceListName}
                    discountPercent={item.discountPercent}
                    translations={priceListDiscountLabel}
                  />
                </span>
              </div>
            );
          })}
          {incomingTireSeason && (
            <div className='flex items-center justify-between gap-4'>
              <span>
                <Trans>Tires to storage</Trans>
              </span>
              <NavioTireSeasonChip
                season={incomingTireSeason}
                size='sm'
                translations={getCommonTranslations().seasonBadge}
              />
            </div>
          )}
        </div>
      </div>

      {discounts && discounts.length > 0 && (
        <div className='flex flex-col gap-1'>
          {discounts.map((discount) => (
            <CheckoutDiscountRow
              key={discount.key}
              label={discount.label}
              amount={discount.amount}
              imageUrl={discount.imageUrl}
            />
          ))}
        </div>
      )}

      <NoddiSeparator />
      <div className='flex justify-between font-semibold'>
        <span>
          <Trans>Total sum including VAT</Trans>
        </span>
        <span>{formatCurrencyAmount({ money: totalPrice })}</span>
      </div>
      {footerNotice}
    </NoddiCard>
  );
};
